<template>
  <div class="space-y-6">
    <!-- Champs Prénom et Nom -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      <!-- Champ Prénom -->
      <div class="space-y-2">
        <label for="firstName" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Prénom
        </label>
        <div class="relative group">
          <input
            id="firstName"
            v-model="firstName"
            type="text"
            class="w-full px-4 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
            :class="localErrors.firstName ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
            placeholder="Votre prénom"
            required
          />
          <!-- Icône de validation -->
          <div
            v-if="firstName && !localErrors.firstName"
            class="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        <div v-if="localErrors.firstName" class="flex items-center gap-2 text-red-500 text-sm">
          <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ localErrors.firstName }}
        </div>
      </div>

      <!-- Champ Nom -->
      <div class="space-y-2">
        <label for="lastName" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Nom
        </label>
        <div class="relative group">
          <input
            id="lastName"
            v-model="lastName"
            type="text"
            class="w-full px-4 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
            :class="localErrors.lastName ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
            placeholder="Votre nom"
            required
          />
          <!-- Icône de validation -->
          <div
            v-if="lastName && !localErrors.lastName"
            class="absolute right-3 top-1/2 transform -translate-y-1/2"
          >
            <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        <div v-if="localErrors.lastName" class="flex items-center gap-2 text-red-500 text-sm">
          <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ localErrors.lastName }}
        </div>
      </div>
    </div>

    <!-- Champ Email -->
    <div class="space-y-2">
      <label for="email" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
        </svg>
        Adresse Email
      </label>
      <div class="relative group">
        <input
          id="email"
          v-model="email"
          type="email"
          class="w-full px-4 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
          :class="localErrors.email ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
          placeholder="votre.email@exemple.com"
          required
        />
        <!-- Icône de validation -->
        <div
          v-if="email && !localErrors.email"
          class="absolute right-3 top-1/2 transform -translate-y-1/2"
        >
          <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
      <div v-if="localErrors.email" class="flex items-center gap-2 text-red-500 text-sm">
        <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ localErrors.email }}
      </div>
    </div>

    <!-- Champ Téléphone -->
    <div class="space-y-2">
      <label for="phone" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
        Numéro de téléphone
      </label>
      <div class="relative group">
        <input
          id="phone"
          v-model="phone"
          type="tel"
          class="w-full px-4 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
          :class="localErrors.phone ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
          placeholder="+237 6 XX XX XX XX"
          required
        />
        <!-- Icône de validation -->
        <div
          v-if="phone && !localErrors.phone"
          class="absolute right-3 top-1/2 transform -translate-y-1/2"
        >
          <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
      <div v-if="localErrors.phone" class="flex items-center gap-2 text-red-500 text-sm">
        <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ localErrors.phone }}
      </div>
    </div>

    <!-- Champ Mot de passe -->
    <div class="space-y-2">
      <label for="regPassword" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Mot de passe
      </label>
      <div class="relative group">
        <input
          id="regPassword"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          class="w-full px-4 py-3.5 pr-12 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
          :class="localErrors.password ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
          placeholder="••••••••"
          required
        />
        <!-- Bouton toggle password -->
        <button
          type="button"
          @click="togglePasswordVisibility"
          class="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        >
          <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
          </svg>
          <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
      </div>
      <div v-if="localErrors.password" class="flex items-center gap-2 text-red-500 text-sm">
        <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ localErrors.password }}
      </div>
    </div>

    <!-- Champ Confirmer le mot de passe -->
    <div class="space-y-2">
      <label for="password2" class="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Confirmer le mot de passe
      </label>
      <div class="relative group">
        <input
          id="password2"
          v-model="password2"
          :type="showPassword ? 'text' : 'password'"
          class="w-full px-4 py-3.5 text-sm bg-gray-50 border-2 border-gray-200 rounded-xl focus:bg-white focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 placeholder-gray-400"
          :class="localErrors.password2 ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-500/20' : 'group-hover:border-gray-300'"
          placeholder="Confirmez votre mot de passe"
          required
        />
        <!-- Icône de validation -->
        <div
          v-if="password2 && !localErrors.password2 && password2 == password"
          class="absolute right-3 top-1/2 transform -translate-y-1/2"
        >
          <svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
      <div v-if="localErrors.password2" class="flex items-center gap-2 text-red-500 text-sm">
        <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
        {{ localErrors.password2 }}
      </div>
    </div>

    <!-- Bouton S'inscrire -->
    <button
      type="button"
      @click="handleSubmit"
      class="group relative cursor-pointer w-full bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-blue-500/20"
      :disabled="isLoading"
    >
      <div class="flex items-center justify-center gap-3">
        <div v-if="isLoading" class="w-5 h-5">
          <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <svg v-else class="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
        <span>{{ isLoading ? 'Inscription...' : "S'inscrire" }}</span>
      </div>
      <!-- Effet de brillance au survol -->
      <div class="absolute inset-0 rounded-xl bg-gradient-to-r from-white/0 via-white/10 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 group-hover:animate-pulse"></div>
    </button>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'

const props = defineProps({
  isLoading: Boolean,
  showPassword: Boolean,
  togglePasswordVisibility: Function,
})

const emit = defineEmits(['register-submit'])

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const phone = ref('')
const password = ref('')
const password2 = ref('')

const localErrors = reactive({})

const validateForm = () => {
  localErrors.firstName = ''
  localErrors.lastName = ''
  localErrors.email = ''
  localErrors.phone = ''
  localErrors.password = ''
  localErrors.password2 = ''

  if (!firstName.value.trim()) {
    localErrors.firstName = 'Le prénom est requis'
  }

  if (!lastName.value.trim()) {
    localErrors.lastName = 'Le nom est requis'
  }

  if (!email.value.trim()) {
    localErrors.email = "L'email est requis"
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
    localErrors.email = "L'email n'est pas valide"
  }

  if (!phone.value.trim()) {
    localErrors.phone = 'Le numéro de téléphone est requis'
  } else if (!/^[\d\s+()-]{8,}$/.test(phone.value)) {
    localErrors.phone = "Le numéro de téléphone n'est pas valide"
  }

  if (!password.value) {
    localErrors.password = 'Le mot de passe est requis'
  } else if (password.value.length < 8) {
    localErrors.password = 'Le mot de passe doit contenir au moins 8 caractères'
  }

  if (password.value !== password2.value) {
    localErrors.password2 = 'Les mots de passe ne correspondent pas'
  }

  return Object.keys(localErrors).every((key) => !localErrors[key])
}

const handleSubmit = () => {
  if (validateForm()) {
    emit('register-submit', {
      first_name: firstName.value,
      last_name: lastName.value,
      username: email.value,
      email: email.value,
      phone: phone.value,
      password: password.value,
    })
  }
}
</script>

<style scoped></style>
